import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, merge, mergeMap, of, pipe, switchMap, take, tap } from "rxjs";
import * as DeviceManufacturerActions from "./device-manufacturers-actions";
import { AppNotificationService } from "src/app/shared/services/notification.service";
import { translate } from '@jsverse/transloco';
import { IDeviceManufacturer } from "src/app/devices/models/device-manufacturer";
import { DeviceService } from "src/app/devices/services/device.service";

@Injectable()
export class DeviceManufacturersEffets {

    ///////////////////////////////
    // GET DEVICE MANUFACTURERS //
    /////////////////////////////

    // Get Device Manufacturers
    getDeviceManufacturers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceManufacturerActions.getDeviceManufacturers), 
            mergeMap(() => {
            return this.deviceService.getDeviceManufacturers({pageNumber: 0, pageSize: 250}).pipe(
                map((response) => 
                    DeviceManufacturerActions.getDeviceManufacturersSuccess({ deviceManufacturers: response.data, total: response.totalCount })
                ), 
                catchError((error) =>
                    of(DeviceManufacturerActions.getDeviceManufacturersFailure({ error: error.message }))
                )
            );
        }),
        take(1)
        )
    );

    
    ///////////////////////////////
    // Search DEVICE MANUFACTURERS //
    /////////////////////////////

    // Get Device Manufacturers
    searchDeviceManufacturers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceManufacturerActions.searchDeviceManufacturers), 
            mergeMap((action) => {
            return this.deviceService.getDeviceManufacturers(action.request).pipe(
                map((response) => 
                    DeviceManufacturerActions.searchDeviceManufacturersSuccess({ deviceManufacturers: response.data, total: response.totalCount })
                ), 
                catchError((error) =>
                    of(DeviceManufacturerActions.searchDeviceManufacturersFailure({ error: error.message }))
                )
            );
        })
        )
    );


    /////////////////////////////////
    // CREATE DEVICE MANUFACTURER //
    ///////////////////////////////

    // Create Device Manufacturer
    createDeviceManufacturer$ = createEffect(() =>
      this.actions$.pipe(
          ofType(DeviceManufacturerActions.createDeviceManufacturer),
          mergeMap((action) => 
              this.deviceService.createDeviceManufacturer(action.deviceManufacturer).pipe(
                  map((deviceManufacturer: IDeviceManufacturer) => DeviceManufacturerActions.createDeviceManufacturerSuccess({ deviceManufacturer })),
                  catchError((error) => of(DeviceManufacturerActions.createDeviceManufacturerFailure({ error: error.message })))
              )
          )
      )
    );

    // Device Manufacturer created successfully
    createDeviceManufacturerSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(DeviceManufacturerActions.createDeviceManufacturerSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('settings.deviceManufacturer.createSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Device Manufacturer not created successfully
    createDeviceManufacturerFailure$ = createEffect(() => 
      this.actions$.pipe(
          ofType(DeviceManufacturerActions.createDeviceManufacturerFailure),
      ), { dispatch: false }
    );




    /////////////////////////////////
    // UPDATE DEVICE MANUFACTURER //
    ///////////////////////////////

    // Update Device Manufacturer
    updateDeviceManufacturer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceManufacturerActions.updateDeviceManufacturer),
            mergeMap((action) => 
                this.deviceService.updateDeviceManufacturer(action.deviceManufacturer).pipe(
                    map((deviceManufacturer: IDeviceManufacturer) => DeviceManufacturerActions.updateDeviceManufacturerSuccess({ deviceManufacturer })),
                    catchError((error) => of(DeviceManufacturerActions.updateDeviceManufacturerFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Update Device Manufacturer successful
    updateDeviceManufacturerSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(DeviceManufacturerActions.updateDeviceManufacturerSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('settings.deviceManufacturer.updateSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Device Manufacturer not updated successfully
    updateDeviceManufacturerFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(DeviceManufacturerActions.updateDeviceManufacturerFailure)
        ), { dispatch: false }
    );




    /////////////////////////////////
    // DELETE DEVICE MANUFACTURER //
    ///////////////////////////////

    // Delete Device Manufacturer
    deleteDeviceManufacturer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceManufacturerActions.deleteDeviceManufacturer), 
            mergeMap((action) => {
                return this.deviceService.deleteDeviceManufacturer(action.id).pipe(
                    map(() => DeviceManufacturerActions.deleteDeviceManufacturerSuccess({id: action.id})), 
                    catchError((error) => of(DeviceManufacturerActions.deleteDeviceManufacturerFailure({ error: error.message })))
            )}
        ))
    );

    // Device Manufacturer deleted successfully
    deleteDeviceManufacturerSuccess$ = createEffect(() => 
      this.actions$.pipe(
          ofType(DeviceManufacturerActions.deleteDeviceManufacturerSuccess),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('settings.deviceManufacturer.deleteSuccess'), style: 'success' });
          })
      ), { dispatch: false }
    );

    // Device Manufacturer not deleted successfully
    deleteDeviceManufacturerFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DeviceManufacturerActions.deleteDeviceManufacturerFailure)
      ), { dispatch: false }
    );




    constructor(
        private actions$: Actions, 
        private deviceService: DeviceService,
        private appnotificationService: AppNotificationService) {}
}